import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, MapPin, Clock, User, Phone, Send, CheckCircle2, ChevronLeft, ChevronRight, Info, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import Seo from '../components/Seo';
import AddressAutocomplete from '../components/AddressAutocomplete';
import PaymentOptions from '../components/PaymentOptions';
import { trackBookingFormSubmit } from '../lib/analytics';

/**
 * Book a Ride
 *
 * Controlled refactor — preserves:
 *   - Existing MoveraCare hero, sidebar cards, brand colors, layout grid
 *   - The API contract (POST /api/bookings, snake_case body)
 *   - Existing data-testids: booking-disclaimer, booking-success-card,
 *     booking-ref, booking-success-message, booking-warning-message
 *   - Field names in state (patientName, facilityName, pickupAddress, ...)
 *     wired to the same backend field mapping
 *   - Seo tag, canonical URL, hero text
 *
 * Adds (per approved requirements):
 *   1. Houston (America/Chicago) date/time validation with field-level errors
 *   2. Today / Tomorrow / Choose Date quick-select
 *   3. Trip type: One Way / Round Trip / Wait & Return  (+ return time UX)
 *   4. Address autocomplete for pickup + destination with separate unit fields
 *   5. Field-level errors, focus management, aria wiring, keyboard support
 *   6. Review step before final submission (Details → Review → Success)
 *   7. Idempotency: crypto.randomUUID() sent as Idempotency-Key header
 *   8. "Request Received" screen — never "Confirmed"
 */

const API = process.env.REACT_APP_BACKEND_URL;

// ─── America/Chicago time helpers (client-side hints only — server is the guard) ───
const CHICAGO_TZ = 'America/Chicago';

function chicagoParts(d = new Date()) {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: CHICAGO_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const parts = Object.fromEntries(fmt.formatToParts(d).map((p) => [p.type, p.value]));
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,      // YYYY-MM-DD
    time: `${parts.hour === '24' ? '00' : parts.hour}:${parts.minute}`,
    weekday: new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: CHICAGO_TZ }).format(d),
    hour: Number(parts.hour === '24' ? '0' : parts.hour),
  };
}

function chicagoToday() { return chicagoParts().date; }
function chicagoTomorrow() {
  const parts = chicagoParts();
  const [y, m, d] = parts.date.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + 1);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function isPastDateChicago(dateStr) {
  if (!dateStr) return false;
  return dateStr < chicagoToday();
}

function isSameDayChicago(dateStr) {
  return !!dateStr && dateStr === chicagoToday();
}

function timeIsAfterChicagoNow(dateStr, timeStr) {
  if (!dateStr || !timeStr) return false;
  const now = chicagoParts();
  if (dateStr > now.date) return true;
  if (dateStr < now.date) return false;
  return timeStr > now.time;
}

function isSundayChicago(dateStr) {
  if (!dateStr) return false;
  const [y, m, d] = dateStr.split('-').map(Number);
  // The date is already a calendar date (no timezone). Sunday = 0.
  return new Date(y, m - 1, d).getDay() === 0;
}

function isAfterHours(timeStr, dateStr) {
  if (!timeStr) return false;
  const [h] = timeStr.split(':').map(Number);
  // Sunday window is narrower — 12:00–17:00 (12 PM – 5 PM).
  if (dateStr && isSundayChicago(dateStr)) {
    return h < 12 || h >= 17;
  }
  // Mon–Sat window is 06:00–19:00 (6 AM – 7 PM).
  return h < 6 || h >= 19;
}

function formatDateForDisplay(dateStr) {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatTimeForDisplay(timeStr) {
  if (!timeStr) return '—';
  const [h, m] = timeStr.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

// ─── Trip type constants preserving backend return_trip mapping ───
const TRIP_TYPES = [
  { value: 'one_way', label: 'One Way', hint: 'Single pickup and drop-off.' },
  { value: 'round_trip', label: 'Round Trip', hint: 'Same driver returns you at a set time.' },
  { value: 'wait_and_return', label: 'Wait & Return', hint: 'Driver waits at destination and returns. Approval + wait charges may apply.' },
];

const DEFAULT_FORM = {
  patientName: '',
  facilityName: '',
  pickupAddress: '',
  pickupUnit: '',
  pickupPlaceId: null,
  pickupLat: null,
  pickupLng: null,
  pickupManual: false,
  dropoffAddress: '',
  dropoffUnit: '',
  dropoffPlaceId: null,
  dropoffLat: null,
  dropoffLng: null,
  dropoffManual: false,
  date: '',
  time: '',
  mobilityType: '',
  tripType: 'one_way',
  returnTime: '',
  returnTimeUnknown: false,
  specialInstructions: '',
  contactPhone: '',
  contactEmail: '',
  smsConsent: false,
};

// Displayed and stored verbatim on opt-in — never pre-selected.
const SMS_CONSENT_TEXT = (
  'I agree to receive operational text messages from MoveraCare Mobility about '
  + 'this transportation request (confirmation and reminders) at the phone number '
  + 'I provided. Message and data rates may apply. Reply STOP to opt out at any time. '
  + 'These messages are operational only and will not include diagnosis, treatment, '
  + 'or medical-condition information.'
);

const FIELD_ORDER = [
  'patientName', 'pickupAddress', 'dropoffAddress',
  'date', 'time', 'mobilityType', 'returnTime', 'contactPhone',
];

const BookRide = () => {
  const [step, setStep] = useState('details'); // details | review
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const idempotencyKeyRef = useRef(null);
  const inputRefs = useRef({});

  // Prepare a fresh idempotency key on mount and after a successful submit.
  useEffect(() => {
    idempotencyKeyRef.current =
      (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }, [submitted]);

  const todayStr = useMemo(() => chicagoToday(), []);
  const tomorrowStr = useMemo(() => chicagoTomorrow(), []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear the field's error when the user edits it.
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleAddressChange = (kind, patch) => {
    // kind === 'pickup' | 'dropoff'
    setFormData((prev) => ({
      ...prev,
      [`${kind}Address`]: patch.address,
      [`${kind}PlaceId`]: patch.place_id,
      [`${kind}Lat`]: patch.lat,
      [`${kind}Lng`]: patch.lng,
      [`${kind}Manual`]: !!patch.manual,
    }));
    const errKey = kind === 'pickup' ? 'pickupAddress' : 'dropoffAddress';
    setErrors((prev) => (prev[errKey] ? { ...prev, [errKey]: undefined } : prev));
  };

  const validateDetails = () => {
    const e = {};
    if (!formData.patientName.trim()) e.patientName = 'Patient name is required.';
    if (!formData.pickupAddress.trim()) e.pickupAddress = 'Pickup address is required.';
    if (!formData.dropoffAddress.trim()) e.dropoffAddress = 'Destination address is required.';
    if (!formData.date) {
      e.date = 'Transportation date is required.';
    } else if (isPastDateChicago(formData.date)) {
      e.date = 'Transportation date cannot be in the past.';
    }
    if (!formData.time) {
      e.time = 'Pickup time is required.';
    } else if (
      formData.date &&
      isSameDayChicago(formData.date) &&
      !timeIsAfterChicagoNow(formData.date, formData.time)
    ) {
      e.time = 'For same-day requests, pickup time must be later than the current Houston time.';
    }
    if (!formData.mobilityType) e.mobilityType = 'Please select a mobility type.';
    if (formData.tripType === 'round_trip' && !formData.returnTime && !formData.returnTimeUnknown) {
      e.returnTime = 'Enter a return time or select "Return time unknown."';
    }
    if (!formData.contactPhone.trim()) e.contactPhone = 'A contact phone is required.';
    setErrors(e);
    return e;
  };

  const focusFirstError = (e) => {
    for (const f of FIELD_ORDER) {
      if (e[f]) {
        const el = inputRefs.current[f] || document.getElementById(f);
        if (el && el.focus) el.focus();
        break;
      }
    }
  };

  const goToReview = (evt) => {
    evt.preventDefault();
    const e = validateDetails();
    if (Object.keys(e).length > 0) {
      focusFirstError(e);
      toast.error('Please correct the highlighted fields.');
      return;
    }
    setStep('review');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submitBooking = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    // Preserve legacy return_trip mapping so existing dashboard filters keep working.
    const legacyReturnTrip = formData.tripType === 'one_way' ? 'no' : 'yes';

    const payload = {
      patient_name: formData.patientName,
      facility_name: formData.facilityName || null,
      pickup_address: formData.pickupAddress,
      dropoff_address: formData.dropoffAddress,
      date: formData.date,
      time: formData.time,
      mobility_type: formData.mobilityType,
      return_trip: legacyReturnTrip,
      trip_type: formData.tripType,
      return_time: formData.tripType === 'round_trip' && !formData.returnTimeUnknown ? formData.returnTime : null,
      return_time_unknown: formData.tripType === 'round_trip' ? !!formData.returnTimeUnknown : false,
      pickup_unit: formData.pickupUnit || null,
      destination_unit: formData.dropoffUnit || null,
      pickup_place_id: formData.pickupPlaceId,
      destination_place_id: formData.dropoffPlaceId,
      pickup_lat: formData.pickupLat,
      pickup_lng: formData.pickupLng,
      destination_lat: formData.dropoffLat,
      destination_lng: formData.dropoffLng,
      pickup_manual: !!formData.pickupManual,
      destination_manual: !!formData.dropoffManual,
      special_instructions: formData.specialInstructions || null,
      contact_phone: formData.contactPhone,
      contact_email: formData.contactEmail || null,
      sms_consent: !!formData.smsConsent,
      sms_consent_text: formData.smsConsent ? SMS_CONSENT_TEXT : null,
    };

    try {
      const res = await fetch(`${API}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKeyRef.current,
        },
        body: JSON.stringify(payload),
      });

      if (res.status === 422) {
        const err = await res.json().catch(() => ({}));
        const detail = err?.detail;
        if (detail && typeof detail === 'object' && detail.field && detail.message) {
          setErrors((prev) => ({ ...prev, [detail.field === 'date_time' ? 'date' : detail.field]: detail.message }));
          setStep('details');
          toast.error(detail.message);
          return;
        }
        toast.error(typeof detail === 'string' ? detail : 'Your request could not be saved. Please review your entries.');
        setStep('details');
        return;
      }

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = await res.json();

      setSubmitted({
        ref: result.ref,
        submission_time: result.submission_time,
        status: result.status,
        contact_email: formData.contactEmail,
        contact_phone: formData.contactPhone,
        notification: result.notification,
        approval_notes: result.approval_notes || [],
      });
      if (result.status === 'received') {
        trackBookingFormSubmit({
          service_type: formData.serviceType || 'unknown',
          source_page: 'book_ride',
          status: 'received',
        });
        toast.success(`Request received (Ref ${result.ref}). Dispatch will contact you at ${formData.contactPhone}.`);
      } else if (result.status === 'received_email_failed') {
        trackBookingFormSubmit({
          service_type: formData.serviceType || 'unknown',
          source_page: 'book_ride',
          status: 'received_email_failed',
        });
        toast.warning(`Request saved (Ref ${result.ref}) — please call 832-925-2128 to confirm receipt.`);
      }
      setStep('details');
      setFormData(DEFAULT_FORM);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (_err) {
      toast.error('Your request could not be saved. Please call 832-925-2128 directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const showSameDayNotice = isSameDayChicago(formData.date);
  const showAfterHoursNotice = !!formData.time && isAfterHours(formData.time, formData.date);
  // Sunday is now a regular service day (12:00 PM – 5:00 PM); an
  // out-of-window pickup will surface through the after-hours notice.
  const showApprovalNotice = showAfterHoursNotice;

  return (
    <div className="min-h-screen pt-24 bg-gradient-to-b from-gray-50 to-white">
      <Seo
        title="Book Non-Emergency Medical Transportation Houston | MoveraCare"
        description="Book a wheelchair or ambulatory non-emergency medical transportation ride with MoveraCare Mobility across Greater Houston. Simple online booking with responsive dispatch."
        path="/book"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Book a Ride', url: '/book' }]}
      />
      <section className="py-12 bg-[#004C97]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Book Your Ride</h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            Schedule reliable medical transportation with MoveraCare Mobility
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {submitted && (
          <div
            className={`mb-8 rounded-2xl border-2 p-6 md:p-8 shadow-lg ${
              submitted.status === 'received'
                ? 'border-[#F9A825] bg-gradient-to-br from-white to-[#FFF8E1]'
                : 'border-orange-400 bg-orange-50'
            }`}
            data-testid="booking-success-card"
            role="status"
            aria-live="polite"
          >
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                submitted.status === 'received' ? 'bg-[#F9A825]' : 'bg-orange-500'
              }`}>
                <CheckCircle2 className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold tracking-wider text-[#004C97] mb-1" data-testid="booking-ref">
                  REQUEST RECEIVED · REF #{submitted.ref}
                </p>
                <h3 className="text-xl md:text-2xl font-bold text-[#004C97] mb-2">
                  Request Received
                </h3>
                {submitted.status === 'received' ? (
                  <p className="text-gray-700 leading-relaxed" data-testid="booking-success-message">
                    Your transportation is <strong>not yet confirmed</strong>. MoveraCare dispatch will review
                    availability and contact you at <strong>{submitted.contact_phone}</strong>.
                    {submitted.contact_email && (
                      <> A minimal acknowledgement (reference number only) was sent to <strong>{submitted.contact_email}</strong>.</>
                    )}
                  </p>
                ) : (
                  <p className="text-gray-800 leading-relaxed" data-testid="booking-warning-message">
                    We saved your request but couldn&apos;t deliver the automated dispatch notification. Please
                    call <a href="tel:+18329252128" className="font-bold underline">832-925-2128</a> and
                    reference <strong>{submitted.ref}</strong> so we can confirm receipt.
                  </p>
                )}
                {submitted.approval_notes && submitted.approval_notes.length > 0 && (
                  <ul className="mt-3 space-y-1 text-sm text-[#004C97]" data-testid="booking-approval-notes">
                    {submitted.approval_notes.map((n) => (
                      <li key={n} className="flex items-start gap-2"><Info className="w-4 h-4 mt-0.5 text-[#F9A825] flex-shrink-0" />{n}</li>
                    ))}
                  </ul>
                )}
                <p className="text-xs text-gray-500 mt-3">
                  <strong>For medical emergencies, call 911.</strong> MoveraCare provides non-emergency medical
                  transportation only. Need help? Call <a href="tel:+18329252128" className="font-semibold text-[#004C97] underline">832-925-2128</a>.
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Card className="border-2 border-gray-200 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-[#004C97] to-[#003D7A] text-white rounded-t-lg">
                <CardTitle className="text-2xl flex items-center">
                  <Calendar className="w-6 h-6 mr-3" />
                  {step === 'review' ? 'Review Your Request' : 'Transportation Request Form'}
                </CardTitle>
                <CardDescription className="text-gray-200">
                  {step === 'review'
                    ? 'Confirm your trip details before submitting. You can return and edit any section.'
                    : 'Please provide detailed information for your transportation needs. All fields marked with * are required.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 md:p-8">
                <div className="mb-6 rounded-lg border-l-4 border-[#F9A825] bg-[#FFF8E1] p-4" data-testid="booking-disclaimer">
                  <p className="text-sm text-[#004C97] font-semibold mb-1">
                    Submitting a request does not confirm transportation.
                  </p>
                  <p className="text-xs text-gray-700 leading-relaxed">
                    MoveraCare dispatch will review availability and contact you at the phone number you provide.
                    For medical emergencies, call 911. For same-day help, call{' '}
                    <a href="tel:+18329252128" className="font-semibold text-[#004C97] underline">832-925-2128</a>.
                  </p>
                </div>

                {step === 'details' && (
                  <form onSubmit={goToReview} className="space-y-8" noValidate>
                    <SectionHeader icon={User} title="Patient Information" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FieldWrapper label="Patient Name *" id="patientName" error={errors.patientName}>
                        <Input
                          id="patientName"
                          value={formData.patientName}
                          onChange={(e) => handleChange('patientName', e.target.value)}
                          placeholder="Full name"
                          className="border-2 focus:border-[#F9A825]"
                          aria-invalid={!!errors.patientName}
                          aria-describedby={errors.patientName ? 'patientName-error' : undefined}
                          ref={(el) => (inputRefs.current.patientName = el)}
                          data-testid="patient-name"
                          required
                        />
                      </FieldWrapper>
                      <FieldWrapper label="Facility Name (if applicable)" id="facilityName">
                        <Input
                          id="facilityName"
                          value={formData.facilityName}
                          onChange={(e) => handleChange('facilityName', e.target.value)}
                          placeholder="Hospital or facility name"
                          className="border-2 focus:border-[#F9A825]"
                          data-testid="facility-name"
                        />
                      </FieldWrapper>
                    </div>

                    <SectionHeader icon={MapPin} title="Pickup & Destination" />
                    <AddressAutocomplete
                      label="Pickup Address"
                      idPrefix="pickupAddress"
                      testId="pickup-address"
                      value={formData.pickupAddress}
                      onChange={(p) => handleAddressChange('pickup', p)}
                      unit={formData.pickupUnit}
                      onUnitChange={(v) => handleChange('pickupUnit', v)}
                      error={errors.pickupAddress}
                      required
                    />
                    <AddressAutocomplete
                      label="Destination Address"
                      idPrefix="dropoffAddress"
                      testId="dropoff-address"
                      value={formData.dropoffAddress}
                      onChange={(p) => handleAddressChange('dropoff', p)}
                      unit={formData.dropoffUnit}
                      onUnitChange={(v) => handleChange('dropoffUnit', v)}
                      error={errors.dropoffAddress}
                      required
                    />

                    <SectionHeader icon={Clock} title="Schedule" />
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2" role="group" aria-label="Quick date selection">
                        <QuickDateButton
                          active={formData.date === todayStr}
                          onClick={() => handleChange('date', todayStr)}
                          testId="date-quick-today"
                        >
                          Today
                        </QuickDateButton>
                        <QuickDateButton
                          active={formData.date === tomorrowStr}
                          onClick={() => handleChange('date', tomorrowStr)}
                          testId="date-quick-tomorrow"
                        >
                          Tomorrow
                        </QuickDateButton>
                        <QuickDateButton
                          active={!!formData.date && formData.date !== todayStr && formData.date !== tomorrowStr}
                          onClick={() => inputRefs.current.date?.focus()}
                          testId="date-quick-choose"
                        >
                          Choose Date
                        </QuickDateButton>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <FieldWrapper label="Transportation Date *" id="date" error={errors.date}>
                          <Input
                            id="date"
                            type="date"
                            min={todayStr}
                            value={formData.date}
                            onChange={(e) => handleChange('date', e.target.value)}
                            className="border-2 focus:border-[#F9A825]"
                            aria-invalid={!!errors.date}
                            aria-describedby={errors.date ? 'date-error' : 'date-help'}
                            ref={(el) => (inputRefs.current.date = el)}
                            data-testid="date-input"
                            required
                          />
                          <p id="date-help" className="text-xs text-gray-500">Houston (Central) time. Yesterday and earlier dates are not accepted.</p>
                        </FieldWrapper>
                        <FieldWrapper label="Pickup Time *" id="time" error={errors.time}>
                          <Input
                            id="time"
                            type="time"
                            value={formData.time}
                            onChange={(e) => handleChange('time', e.target.value)}
                            className="border-2 focus:border-[#F9A825]"
                            aria-invalid={!!errors.time}
                            aria-describedby={errors.time ? 'time-error' : 'time-help'}
                            ref={(el) => (inputRefs.current.time = el)}
                            data-testid="time-input"
                            required
                          />
                          <p id="time-help" className="text-xs text-gray-500">
                            {formData.time ? `Displayed as ${formatTimeForDisplay(formData.time)} (Houston/Central).` : '24-hour input; AM/PM shown at review.'}
                          </p>
                        </FieldWrapper>
                      </div>
                      {showSameDayNotice && (
                        <Notice tone="info" testId="notice-same-day">
                          Same-day transportation is subject to dispatch availability.
                        </Notice>
                      )}
                      {showApprovalNotice && (
                        <Notice tone="warn" testId="notice-approval">
                          This request is outside standard service hours. Regular service hours are Mon–Sat 6:00 AM – 7:00 PM and Sunday 12:00 PM – 5:00 PM.
                        </Notice>
                      )}
                    </div>

                    <SectionHeader icon={Calendar} title="Trip Type" />
                    <fieldset className="space-y-2" data-testid="trip-type-fieldset">
                      <legend className="sr-only">Trip type</legend>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {TRIP_TYPES.map((t) => (
                          <label
                            key={t.value}
                            htmlFor={`trip-${t.value}`}
                            className={`relative flex flex-col rounded-lg border-2 p-4 cursor-pointer transition-colors ${
                              formData.tripType === t.value
                                ? 'border-[#F9A825] bg-[#FFF8E1]'
                                : 'border-gray-200 hover:border-[#F9A825]/60'
                            }`}
                            data-testid={`trip-type-${t.value}`}
                          >
                            <input
                              type="radio"
                              id={`trip-${t.value}`}
                              name="tripType"
                              value={t.value}
                              checked={formData.tripType === t.value}
                              onChange={() => handleChange('tripType', t.value)}
                              className="sr-only"
                            />
                            <span className="text-sm font-semibold text-[#004C97]">{t.label}</span>
                            <span className="text-xs text-gray-600 mt-1">{t.hint}</span>
                          </label>
                        ))}
                      </div>
                      {formData.tripType === 'round_trip' && (
                        <div className="pt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <FieldWrapper label="Return Time" id="returnTime" error={errors.returnTime}>
                            <Input
                              id="returnTime"
                              type="time"
                              value={formData.returnTime}
                              onChange={(e) => handleChange('returnTime', e.target.value)}
                              disabled={formData.returnTimeUnknown}
                              className="border-2 focus:border-[#F9A825]"
                              aria-invalid={!!errors.returnTime}
                              ref={(el) => (inputRefs.current.returnTime = el)}
                              data-testid="return-time-input"
                            />
                          </FieldWrapper>
                          <label className="flex items-start gap-2 pt-8" htmlFor="returnUnknown">
                            <input
                              id="returnUnknown"
                              type="checkbox"
                              checked={formData.returnTimeUnknown}
                              onChange={(e) => handleChange('returnTimeUnknown', e.target.checked)}
                              className="mt-1"
                              data-testid="return-time-unknown"
                            />
                            <span className="text-sm text-gray-700">
                              Return time unknown — <strong>call when ready.</strong>
                            </span>
                          </label>
                        </div>
                      )}
                    </fieldset>

                    <SectionHeader icon={User} title="Mobility & Instructions" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FieldWrapper label="Mobility Type *" id="mobilityType" error={errors.mobilityType}>
                        <Select
                          value={formData.mobilityType}
                          onValueChange={(v) => handleChange('mobilityType', v)}
                        >
                          <SelectTrigger
                            id="mobilityType"
                            className="border-2 focus:border-[#F9A825]"
                            aria-invalid={!!errors.mobilityType}
                            data-testid="mobility-type"
                          >
                            <SelectValue placeholder="Select mobility type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="wheelchair">Wheelchair</SelectItem>
                            <SelectItem value="ambulatory">Ambulatory</SelectItem>
                          </SelectContent>
                        </Select>
                      </FieldWrapper>
                    </div>
                    <FieldWrapper label="Special Instructions" id="specialInstructions">
                      <Textarea
                        id="specialInstructions"
                        value={formData.specialInstructions}
                        onChange={(e) => handleChange('specialInstructions', e.target.value)}
                        placeholder="Please tell us if the passenger uses oxygen or other medical equipment so dispatch can assess whether we can safely serve this trip. Note walker or entrance details as needed. Do not include medical or insurance information."
                        rows={3}
                        className="border-2 focus:border-[#F9A825] resize-none"
                        data-testid="special-instructions"
                      />
                    </FieldWrapper>

                    <SectionHeader icon={Phone} title="Contact" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FieldWrapper label="Contact Phone *" id="contactPhone" error={errors.contactPhone}>
                        <Input
                          id="contactPhone"
                          type="tel"
                          value={formData.contactPhone}
                          onChange={(e) => handleChange('contactPhone', e.target.value)}
                          placeholder="(555) 123-4567"
                          className="border-2 focus:border-[#F9A825]"
                          aria-invalid={!!errors.contactPhone}
                          ref={(el) => (inputRefs.current.contactPhone = el)}
                          data-testid="contact-phone"
                          required
                        />
                      </FieldWrapper>
                      <FieldWrapper label="Contact Email" id="contactEmail">
                        <Input
                          id="contactEmail"
                          type="email"
                          value={formData.contactEmail}
                          onChange={(e) => handleChange('contactEmail', e.target.value)}
                          placeholder="email@example.com"
                          className="border-2 focus:border-[#F9A825]"
                          data-testid="contact-email"
                        />
                      </FieldWrapper>
                    </div>

                    {/* SMS opt-in — NEVER pre-selected */}
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!formData.smsConsent}
                          onChange={(e) => handleChange('smsConsent', e.target.checked)}
                          className="mt-1 h-4 w-4"
                          data-testid="sms-consent-checkbox"
                        />
                        <span className="text-sm text-gray-800 leading-snug">
                          <strong>Optional — text message updates.</strong> {SMS_CONSENT_TEXT}
                        </span>
                      </label>
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-gradient-to-r from-[#F9A825] to-[#E08E1E] text-white hover:from-[#E08E1E] hover:to-[#D07D0D] font-semibold text-lg py-6 shadow-lg"
                      data-testid="review-request-btn"
                    >
                      Review Request
                      <ChevronRight className="ml-2 w-5 h-5" />
                    </Button>
                  </form>
                )}

                {step === 'review' && (
                  <ReviewStep
                    formData={formData}
                    onEdit={() => setStep('details')}
                    onSubmit={submitBooking}
                    submitting={isSubmitting}
                  />
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar — preserved from previous design */}
          <div className="space-y-6">
            <Card className="border-2 border-[#F9A825] shadow-lg bg-gradient-to-br from-[#F9A825]/5 to-white">
              <CardHeader className="bg-[#004C97] text-white">
                <CardTitle className="flex items-center text-lg">
                  <Phone className="w-5 h-5 mr-2" />
                  Need Immediate Help?
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-2 font-medium">Call our dispatch team:</p>
                  <a
                    href="tel:+18329252128"
                    className="text-2xl font-bold text-[#004C97] hover:text-[#F9A825] transition-colors block"
                    data-testid="sidebar-phone"
                  >
                    (832) 925-2128
                  </a>
                </div>
                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-600 mb-2 font-medium">Email us:</p>
                  <a href="mailto:dispatch@moveracaremobility.com" className="text-[#004C97] hover:text-[#F9A825] transition-colors font-medium break-all">
                    dispatch@moveracaremobility.com
                  </a>
                </div>
              </CardContent>
            </Card>
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-lg text-[#004C97]">
                  <Clock className="w-5 h-5 mr-2 text-[#F9A825]" />
                  Booking Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-gray-700">
                <p>24-hour advance notice recommended for wheelchair transport.</p>
                <p>Same-day service subject to dispatch availability.</p>
                <p>Confirmation provided by phone once dispatch reviews your request.</p>
                <p>We serve Houston and surrounding areas.</p>
              </CardContent>
            </Card>
            <Card className="bg-[#004C97] text-white shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg">Service Hours (Houston)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-200 font-medium">Mon–Sat 6:00 AM – 7:00 PM</p>
                <p className="text-sm text-gray-300 mt-2">Sunday 12:00 PM – 5:00 PM.</p>
              </CardContent>
            </Card>
            <PaymentOptions variant="full" />
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Presentational helpers ─────────────────────────────────────────────────

const SectionHeader = ({ icon: Icon, title }) => (
  <div className="flex items-center space-x-2 pb-2 border-b-2 border-[#F9A825]">
    <Icon className="w-5 h-5 text-[#F9A825]" />
    <h3 className="text-lg font-semibold text-[#004C97]">{title}</h3>
  </div>
);

const FieldWrapper = ({ label, id, error, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-[#004C97] font-medium">{label}</Label>
    {children}
    {error && (
      <p id={`${id}-error`} role="alert" className="text-xs text-red-600 flex items-start gap-1" data-testid={`${id}-error`}>
        <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>{error}</span>
      </p>
    )}
  </div>
);

const Notice = ({ tone, children, testId }) => {
  const styles = tone === 'warn'
    ? 'border-orange-500 bg-orange-50 text-orange-900'
    : 'border-[#F9A825] bg-[#FFF8E1] text-[#004C97]';
  return (
    <p
      className={`rounded-lg border-l-4 px-3 py-2 text-sm flex items-start gap-2 ${styles}`}
      role="status"
      data-testid={testId}
    >
      <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <span>{children}</span>
    </p>
  );
};

const QuickDateButton = ({ active, onClick, testId, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-2 rounded-lg border-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-[#F9A825] ${
      active
        ? 'border-[#F9A825] bg-[#F9A825] text-white'
        : 'border-gray-200 text-[#004C97] hover:border-[#F9A825]'
    }`}
    data-testid={testId}
    aria-pressed={active}
  >
    {children}
  </button>
);

const ReviewStep = ({ formData, onEdit, onSubmit, submitting }) => {
  const [affirmed, setAffirmed] = useState(false);
  const legacyReturnTrip = formData.tripType === 'one_way' ? 'No' : 'Yes';
  return (
    <div className="space-y-6" data-testid="booking-review">
      <div className="rounded-lg border-2 border-[#004C97]/10 bg-white">
        <ReviewRow label="Patient">{formData.patientName || '—'}</ReviewRow>
        {formData.facilityName && <ReviewRow label="Facility">{formData.facilityName}</ReviewRow>}
        <ReviewRow label="Trip Type">{TRIP_TYPES.find((t) => t.value === formData.tripType)?.label}</ReviewRow>
        <ReviewRow label="Return Trip?">
          {formData.tripType === 'one_way'
            ? 'One way'
            : formData.tripType === 'round_trip'
              ? (formData.returnTimeUnknown ? 'Round trip — call when ready' : `Round trip @ ${formatTimeForDisplay(formData.returnTime)}`)
              : 'Driver waits and returns'}
        </ReviewRow>
        <ReviewRow label="Date">{formatDateForDisplay(formData.date)}</ReviewRow>
        <ReviewRow label="Pickup Time">{formatTimeForDisplay(formData.time)}</ReviewRow>
        <ReviewRow label="Pickup Address">
          <div>
            <div>{formData.pickupAddress || '—'}</div>
            {formData.pickupUnit && <div className="text-xs text-gray-600 mt-0.5">Unit: {formData.pickupUnit}</div>}
            {formData.pickupManual && <div className="text-xs text-orange-600 mt-0.5">Manual entry — dispatch will verify.</div>}
          </div>
        </ReviewRow>
        <ReviewRow label="Destination">
          <div>
            <div>{formData.dropoffAddress || '—'}</div>
            {formData.dropoffUnit && <div className="text-xs text-gray-600 mt-0.5">Unit: {formData.dropoffUnit}</div>}
            {formData.dropoffManual && <div className="text-xs text-orange-600 mt-0.5">Manual entry — dispatch will verify.</div>}
          </div>
        </ReviewRow>
        <ReviewRow label="Mobility">{formData.mobilityType || '—'}</ReviewRow>
        {formData.specialInstructions && <ReviewRow label="Instructions">{formData.specialInstructions}</ReviewRow>}
        <ReviewRow label="Contact Phone">{formData.contactPhone}</ReviewRow>
        {formData.contactEmail && <ReviewRow label="Contact Email">{formData.contactEmail}</ReviewRow>}
        <ReviewRow label="Legacy return_trip">{legacyReturnTrip}</ReviewRow>
      </div>
      <label className="flex items-start gap-3" htmlFor="affirm-review">
        <input
          id="affirm-review"
          type="checkbox"
          checked={affirmed}
          onChange={(e) => setAffirmed(e.target.checked)}
          className="mt-1"
          data-testid="affirm-review"
        />
        <span className="text-sm text-gray-700">
          I affirm the information above is accurate. I understand submitting this form does not confirm
          transportation and that MoveraCare dispatch will contact me to confirm.
        </span>
      </label>
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={onEdit}
          disabled={submitting}
          className="flex-1 border-2 border-[#004C97] text-[#004C97] hover:bg-[#004C97] hover:text-white"
          data-testid="review-back-btn"
        >
          <ChevronLeft className="mr-2 w-5 h-5" /> Back to Edit
        </Button>
        <Button
          type="button"
          onClick={onSubmit}
          disabled={submitting || !affirmed}
          className="flex-1 bg-gradient-to-r from-[#F9A825] to-[#E08E1E] text-white hover:from-[#E08E1E] hover:to-[#D07D0D] font-semibold text-lg py-6 shadow-lg"
          data-testid="submit-request-btn"
        >
          {submitting ? (
            <><Loader2 className="mr-2 w-5 h-5 animate-spin" /> Submitting…</>
          ) : (
            <><Send className="mr-2 w-5 h-5" /> Submit Request</>
          )}
        </Button>
      </div>
    </div>
  );
};

const ReviewRow = ({ label, children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 px-4 py-2.5 border-b border-gray-100 last:border-b-0">
    <div className="text-xs uppercase tracking-wide text-gray-500 sm:col-span-1">{label}</div>
    <div className="text-sm text-[#004C97] font-medium sm:col-span-2">{children}</div>
  </div>
);

export default BookRide;
